import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Outlet, matchPath, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { commonComponentsRegistry } from '../registry/CommonRegistry';
import { TwoColLayoutProps } from './types';

const Container = commonComponentsRegistry.getLazyComponent('Container');
const AppColumn = commonComponentsRegistry.getLazyComponent('AppColumn');
const MainColumn = commonComponentsRegistry.getLazyComponent('MainColumn');

export function TwoColLayout({
    extensionRoute,
    routes,
    description,
    mode = 'center',
    mainColumnProps,
}: TwoColLayoutProps) {
    const intl = useIntl();
    const { pathname } = useLocation();

    const ROUTES_ARRAY = useMemo(() => Object.values(routes), [routes]);
    const [activeTab, setActiveTab] = useState(ROUTES_ARRAY?.[0]?.key);

    const tabs = ROUTES_ARRAY.map((tab) => ({
        ...tab,
        label: intl.formatMessage({ id: tab.label }),
        href: tab.absolutePath ? tab.path : `/ng/rr/${extensionRoute}/${tab.path}`,
        group: tab.group ? intl.formatMessage({ id: tab.group }) : undefined,
    }));

    useEffect(() => {
        const activeRoute = ROUTES_ARRAY.find((route) => {
            if (`/ng/rr/${extensionRoute}/${route.path}` === pathname) {
                return true;
            }

            return route.paths?.reduce<boolean>((previousValue: boolean, currentValue: string) => {
                const pattern = `/ng/rr/${extensionRoute}/${currentValue}`;
                const isMatch = matchPath(pattern, pathname) !== null;
                return previousValue || isMatch;
            }, false);
        });
        if (activeRoute) setActiveTab(activeRoute.key);
    }, [pathname, ROUTES_ARRAY, extensionRoute]);

    return (
        <Suspense fallback={<></>}>
            <Container mode={mode}>
                <AppColumn mode={mode} description={description} tabs={tabs} activeTab={activeTab || ''} />
                <MainColumn mode={mode} flex={true} {...mainColumnProps}>
                    <Outlet />
                </MainColumn>
            </Container>
        </Suspense>
    );
}
